export const RARITY_ORDER = [
  "common",
  "uncommon",
  "rare",
  "epic",
  "legendary",
  "mythic",
] as const;

export type Rarity = (typeof RARITY_ORDER)[number];

export type TechniqueScrollRarity = "uncommon" | "rare" | "epic" | "legendary";

export interface TechniqueLoadoutSlot {
  slotIndex: number;
  techniqueId: string;
}

export const RARITY_RULES: Record<
  Rarity,
  { potentialMultiplier: number; maxEquippedTechniques: number }
> = {
  common: { potentialMultiplier: 1, maxEquippedTechniques: 1 },
  uncommon: { potentialMultiplier: 1.25, maxEquippedTechniques: 2 },
  rare: { potentialMultiplier: 1.5, maxEquippedTechniques: 3 },
  epic: { potentialMultiplier: 1.75, maxEquippedTechniques: 4 },
  legendary: { potentialMultiplier: 2, maxEquippedTechniques: 5 },
  mythic: { potentialMultiplier: 3, maxEquippedTechniques: 6 },
};

export const RARITY_PRESENTATION: Record<
  Rarity,
  { label: string; color: string }
> = {
  common: { label: "Common", color: "#a9b3b7" },
  uncommon: { label: "Uncommon", color: "#61c888" },
  rare: { label: "Rare", color: "#56a6ff" },
  epic: { label: "Epic", color: "#ff638a" },
  legendary: { label: "Legendary", color: "#ffc04f" },
  mythic: { label: "Mythic", color: "#b17bff" },
};

export function maxEquippedTechniques(rarity: Rarity): number {
  return RARITY_RULES[rarity].maxEquippedTechniques;
}

export function validateLoadout(
  rarity: Rarity,
  known: readonly string[],
  equipped: readonly string[],
): boolean {
  return (
    equipped.length <= maxEquippedTechniques(rarity) &&
    new Set(equipped).size === equipped.length &&
    equipped.every((techniqueId) => known.includes(techniqueId))
  );
}

export function validateLoadoutSlots(
  rarity: Rarity,
  known: readonly string[],
  equipped: readonly TechniqueLoadoutSlot[],
): boolean {
  const capacity = maxEquippedTechniques(rarity);
  return (
    equipped.length <= capacity &&
    new Set(equipped.map(({ slotIndex }) => slotIndex)).size ===
      equipped.length &&
    new Set(equipped.map(({ techniqueId }) => techniqueId)).size ===
      equipped.length &&
    equipped.every(
      ({ slotIndex, techniqueId }) =>
        Number.isInteger(slotIndex) &&
        slotIndex >= 0 &&
        slotIndex < capacity &&
        known.includes(techniqueId),
    )
  );
}

export function assignTechniqueToLoadoutSlot(
  equipped: readonly TechniqueLoadoutSlot[],
  slotIndex: number,
  techniqueId: string,
): TechniqueLoadoutSlot[] {
  return [
    ...equipped.filter(
      (entry) =>
        entry.slotIndex !== slotIndex && entry.techniqueId !== techniqueId,
    ),
    { slotIndex, techniqueId },
  ].sort((left, right) => left.slotIndex - right.slotIndex);
}

export function removeTechniqueFromLoadoutSlot(
  equipped: readonly TechniqueLoadoutSlot[],
  slotIndex: number,
): TechniqueLoadoutSlot[] {
  return equipped.filter((entry) => entry.slotIndex !== slotIndex);
}
